import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { opsGetDashboard, opsListBookings, opsListRides, opsListRequests } from "../../../api/adminOpsApi";

function cls(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Card({ title, value, hint, to, tone = "neutral" }) {
  const toneCls =
    tone === "danger"
      ? "text-red-600"
      : tone === "warn"
        ? "text-amber-600"
        : "text-slate-900";

  const inner = (
    <div className="rounded-xl border border-slate-200 bg-white px-5 py-4">
      <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
      <div className={cls("mt-2 text-3xl font-extrabold", toneCls)}>{value ?? "—"}</div>
      {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
    </div>
  );

  if (!to) return inner;
  return (
    <Link to={to} className="block hover:opacity-[0.98]">
      {inner}
    </Link>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
        {subtitle ? <div className="mt-1 text-sm text-slate-600">{subtitle}</div> : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

export default function OpsDashboard() {
  const [busy, setBusy] = useState(true);
  const [summary, setSummary] = useState({
    liveRequests: 0,
    fulfilledRequests: 0,
    expiredRequests: 0,
    activeBookings: 0,
    unfulfilledBookings: 0,
    completedBookings: 0,
    cancelledBookings: 0,
    ridesInProgress: 0,
    arrivedRides: 0,
    cancellationsToday: 0,
    paymentsStuck: 0,
  });

  const load = async () => {
    setBusy(true);
    try {
      const [requestsRes, activeRes, unfulfilledRes, completedRes, cancelledRes, ridesRes, arrivedRes, dashboardRes] = await Promise.all([
        opsListRequests({ page: 0, size: 300 }),
        opsListBookings({ bucket: "ACTIVE", page: 0, size: 1 }),
        opsListBookings({ bucket: "UNFULFILLED", page: 0, size: 1 }),
        opsListBookings({ bucket: "COMPLETED", page: 0, size: 1 }),
        opsListBookings({ bucket: "CANCELLED", page: 0, size: 1 }),
        opsListRides({ bucket: "IN_PROGRESS", page: 0, size: 1 }),
        opsListRides({ bucket: "ARRIVED", page: 0, size: 1 }),
        opsGetDashboard().catch(() => null),
      ]);

      const requestItems = Array.isArray(requestsRes?.items) ? requestsRes.items : [];
      const next = { liveRequests: 0, fulfilledRequests: 0, expiredRequests: 0 };
      for (const item of requestItems) {
        const status = String(item?.status || "").toUpperCase();
        if (item?.finalBookingId || status.includes("FULFIL")) next.fulfilledRequests += 1;
        else if (status.includes("EXPIRE") || status.includes("CANCEL")) next.expiredRequests += 1;
        else next.liveRequests += 1;
      }

      setSummary({
        liveRequests: next.liveRequests,
        fulfilledRequests: next.fulfilledRequests,
        expiredRequests: next.expiredRequests,
        activeBookings: Number(activeRes?.total || 0),
        unfulfilledBookings: Number(unfulfilledRes?.total || 0),
        completedBookings: Number(completedRes?.total || 0),
        cancelledBookings: Number(cancelledRes?.total || 0),
        ridesInProgress: Number(ridesRes?.total || 0),
        arrivedRides: Number(arrivedRes?.total || 0),
        cancellationsToday: Number(dashboardRes?.kpis?.cancellationsToday || 0),
        paymentsStuck: Number(dashboardRes?.kpis?.paymentsStuckOps || 0),
      });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cards = useMemo(
    () => [
      {
        title: "Live Requests",
        value: summary.liveRequests,
        hint: "Waiting for first driver accept",
        to: "/admin/ops/requests",
        tone: summary.liveRequests > 0 ? "warn" : "neutral",
      },
      {
        title: "Fulfilled Requests",
        value: summary.fulfilledRequests,
        hint: "Already converted into bookings",
        to: "/admin/ops/requests",
      },
      {
        title: "Active Bookings",
        value: summary.activeBookings,
        hint: "Accepted and not yet terminal",
        to: "/admin/ops/bookings",
      },
      {
        title: "Trips In Progress",
        value: summary.ridesInProgress,
        hint: "Live rides under execution",
        to: "/admin/ops/rides",
      },
      {
        title: "Cancelled Bookings",
        value: summary.cancelledBookings,
        hint: "Track who cancelled and why",
        to: "/admin/ops/bookings",
        tone: summary.cancelledBookings > 0 ? "warn" : "neutral",
      },
      {
        title: "Unfulfilled Bookings",
        value: summary.unfulfilledBookings,
        hint: "Need ops review / recovery",
        to: "/admin/ops/bookings",
        tone: summary.unfulfilledBookings > 0 ? "danger" : "neutral",
      },
    ],
    [summary]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold tracking-wider text-slate-500">Ride Now</div>
          <div className="mt-1 text-xl font-semibold tracking-tight text-slate-900">Operational Overview</div>
          <div className="mt-1 text-sm text-slate-600">
            This dashboard follows the real Ride Now path: request, accepted booking, live ride, then completed or cancelled.
          </div>
        </div>
        <button
          className="h-9 rounded-md border border-slate-200 px-3 text-sm font-semibold hover:bg-slate-50"
          onClick={load}
          disabled={busy}
        >
          {busy ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {cards.map((card) => (
          <Card key={card.title} {...card} />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section
          title="Operate By Stage"
          subtitle="Use the screens below in the same order the trip moves through the system."
        >
          <div className="space-y-3">
            <Link to="/admin/ops/requests" className="block rounded-xl border border-slate-200 px-4 py-3 hover:bg-slate-50">
              <div className="text-sm font-semibold text-slate-900">Requests</div>
              <div className="mt-1 text-xs text-slate-600">See live requests, fulfilled requests, expired requests, and what happened after fulfillment.</div>
            </Link>
            <Link to="/admin/ops/bookings" className="block rounded-xl border border-slate-200 px-4 py-3 hover:bg-slate-50">
              <div className="text-sm font-semibold text-slate-900">Bookings</div>
              <div className="mt-1 text-xs text-slate-600">Track accepted bookings, pickup delays, completed bookings, unfulfilled cases, and cancellations with source.</div>
            </Link>
            <Link to="/admin/ops/rides" className="block rounded-xl border border-slate-200 px-4 py-3 hover:bg-slate-50">
              <div className="text-sm font-semibold text-slate-900">Rides</div>
              <div className="mt-1 text-xs text-slate-600">Monitor arrival, live execution, stuck rides, and ride-level ops issues.</div>
            </Link>
          </div>
        </Section>

        <Section
          title="Current Focus"
          subtitle="Only the critical buckets needed for today’s Ride Now flow."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-slate-50 px-4 py-3">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Live requests</div>
              <div className="mt-2 text-2xl font-black text-slate-900">{summary.liveRequests}</div>
              <div className="mt-1 text-xs text-slate-600">Still waiting for driver acceptance.</div>
            </div>
            <div className="rounded-xl bg-slate-50 px-4 py-3">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Arrived rides</div>
              <div className="mt-2 text-2xl font-black text-slate-900">{summary.arrivedRides}</div>
              <div className="mt-1 text-xs text-slate-600">Driver reached pickup but ride not started yet.</div>
            </div>
            <div className="rounded-xl bg-slate-50 px-4 py-3">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Cancellations today</div>
              <div className="mt-2 text-2xl font-black text-slate-900">{summary.cancellationsToday}</div>
              <div className="mt-1 text-xs text-slate-600">Review who cancelled and the reason on bookings and requests.</div>
            </div>
            <div className="rounded-xl bg-slate-50 px-4 py-3">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Payments stuck</div>
              <div className="mt-2 text-2xl font-black text-slate-900">{summary.paymentsStuck}</div>
              <div className="mt-1 text-xs text-slate-600">Payment mismatches that still need ops review.</div>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
